import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaCalculator, FaTimes, FaExternalLinkAlt, FaMapMarkerAlt } from 'react-icons/fa';
import CollapsibleSection from '../../components/shared/CollapsibleSection';
import { formatCurrency } from '../../utils/utils';
import { leadImageUrl } from './leadSubmissionService';

// Structured web-form submissions (Driveway500 calculator) for a lead: the
// customer-drawn outline snapshot plus measurement, online estimate, coupon,
// consent and attribution. Rendered by LeadDetails; hidden when empty.

const SOURCE_LABELS = {
  mrpatch_driveway_calculator: 'Driveway500 Calculator',
};

const PROJECT_LABELS = {
  replacement: 'Replace existing driveway',
  installation: 'Brand-new driveway',
};

const REQUEST_LABELS = {
  instant_estimate: 'Instant estimate',
  onsite_visit: 'On-site visit (skipped measuring)',
};

const CONTACT_LABELS = { text: 'Text', call: 'Phone call', email: 'Email' };

const ATTRIBUTION_LABELS = {
  utm_source: 'Source',
  utm_medium: 'Medium',
  utm_campaign: 'Campaign',
  utm_campaing: 'Campaign',
  utm_content: 'Content',
  utm_term: 'Term',
  gclid: 'GCLID',
  fbclid: 'FBCLID',
};

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const Card = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  gap: ${(p) => p.theme.spacing(2)};
  padding: 20px 30px;
  border-bottom: 1px solid ${(p) => p.theme.colors.inputGray};

  &:last-child { border-bottom: none; }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`;

const Thumb = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid ${(p) => p.theme.colors.inputGray};
  border-radius: 8px;
  overflow: hidden;
  background: #111;
  cursor: zoom-in;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const NoImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 1px dashed ${(p) => p.theme.colors.inputGray};
  border-radius: 8px;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 13px;
  text-align: center;
  padding: 12px;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px 12px;
  margin-bottom: 10px;

  .title { font-weight: 700; color: ${(p) => p.theme.colors.textPrimary}; }
  .date { font-size: 12px; color: ${(p) => p.theme.colors.textSecondary}; }
`;

const Price = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.primary};
  margin-bottom: 4px;

  small {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 4px 12px;
  margin: 10px 0 0;
  font-size: 13px;

  dt { color: ${(p) => p.theme.colors.textSecondary}; }
  dd { margin: 0; color: ${(p) => p.theme.colors.textPrimary}; overflow-wrap: anywhere; }
  a { color: ${(p) => p.theme.colors.primary}; }
`;

const Notes = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff8e1;
  border: 1px solid #ffe8a3;
  border-left: 3px solid #ffb800;
  border-radius: 6px;
  font-size: 13px;
  color: #3a3a3a;
  white-space: pre-wrap;
  line-height: 1.5;

  strong {
    display: block;
    font-size: 11px;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: #8a6500;
    margin-bottom: 4px;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;

  span {
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 999px;
    border: 1px solid ${(p) => p.theme.colors.inputGray};
    color: ${(p) => p.theme.colors.textSecondary};
    overflow-wrap: anywhere;
  }
  b { color: ${(p) => p.theme.colors.textPrimary}; font-weight: 600; }
`;

const Viewer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;

  img {
    max-width: 100%;
    max-height: calc(100vh - 110px);
    object-fit: contain;
    border-radius: 6px;
  }

  .bar {
    display: flex;
    gap: 18px;
    margin-top: 12px;
  }

  a, button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #fff;
    background: none;
    border: none;
    font-size: 14px;
    cursor: pointer;
    text-decoration: none;
  }
`;

// Small thumbnail for a service request card (LeadDetails).
export const SubmissionThumb = ({ image, onOpen }) => {
  if (!image) return null;
  return (
    <Thumb
      type="button"
      style={{ maxWidth: 220, marginTop: 10 }}
      onClick={(e) => { e.stopPropagation(); onOpen(image); }}
      title="View driveway outline"
    >
      <img src={leadImageUrl(image)} alt="Customer-drawn driveway outline" loading="lazy" />
    </Thumb>
  );
};

export const ImageViewer = ({ image, onClose }) => {
  useEffect(() => {
    if (!image) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [image, onClose]);

  if (!image) return null;
  const src = leadImageUrl(image);
  return (
    <Viewer onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <img src={src} alt={image.caption || 'Lead image'} />
      <div className="bar">
        <a href={src} target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt /> Open original</a>
        <button type="button" onClick={onClose}><FaTimes /> Close</button>
      </div>
    </Viewer>
  );
};

const SubmissionCard = ({ submission, onOpenImage }) => {
  const image = (submission.images || [])[0];
  const est = submission.estimate;
  const m = submission.measurement;
  const p = submission.property || {};
  const contact = submission.contact || {};
  const consent = submission.sms_consent || {};
  const attribution = Object.entries(submission.attribution || {}).filter(([, v]) => v);
  const hasCoords = Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)) && p.lat !== null;

  return (
    <Card>
      <div>
        {image ? (
          <Thumb type="button" onClick={() => onOpenImage(image)} title="View full size">
            <img src={leadImageUrl(image)} alt="Customer-drawn driveway outline" loading="lazy" />
          </Thumb>
        ) : (
          <NoImage>
            {submission.request_type === 'onsite_visit'
              ? 'Customer skipped measuring — on-site visit requested.'
              : 'No outline image was captured.'}
          </NoImage>
        )}
      </div>

      <div>
        <Header>
          <span className="title">
            {SOURCE_LABELS[submission.source] || submission.source}
            {submission.service_name ? ` · ${submission.service_name}` : ''}
          </span>
          <span className="date">{formatDateTime(submission.submitted_at || submission.createdAt)}</span>
        </Header>

        {est && est.low && est.high && (
          <Price>
            {formatCurrency(est.low)} – {formatCurrency(est.high)}
            <small>
              Online estimate shown to the customer
              {est.minimumApplied ? ` · ${formatCurrency(est.minimumFee)} minimum applied` : ''}
            </small>
          </Price>
        )}

        <Facts>
          {m && m.area_sqft ? (
            <>
              <dt>Measured area</dt>
              <dd>{Number(m.area_sqft).toLocaleString('en-US')} sq ft{m.source ? ` (drawn on ${m.source})` : ''}</dd>
            </>
          ) : null}
          <dt>Project</dt>
          <dd>{PROJECT_LABELS[submission.project_type] || submission.project_type || '—'}</dd>
          <dt>Request</dt>
          <dd>{REQUEST_LABELS[submission.request_type] || submission.request_type || '—'}</dd>
          {submission.coupon && submission.coupon.code && (
            <>
              <dt>Coupon</dt>
              <dd>
                {submission.coupon.code}
                {submission.coupon.amount ? ` — ${formatCurrency(submission.coupon.amount)} off` : ''}
                {submission.coupon.apply_at === 'final_quote' ? ' (apply on final quote)' : ''}
              </dd>
            </>
          )}
          {(p.label || p.street) && (
            <>
              <dt>Property</dt>
              <dd>
                {p.label || [p.street, p.city, p.state, p.zip].filter(Boolean).join(', ')}
                {hasCoords && (
                  <>
                    {' '}
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaMapMarkerAlt /> Map
                    </a>
                  </>
                )}
              </dd>
            </>
          )}
          {contact.preferred_method && (
            <>
              <dt>Prefers</dt>
              <dd>{CONTACT_LABELS[contact.preferred_method] || contact.preferred_method}</dd>
            </>
          )}
          <dt>SMS consent</dt>
          <dd>
            {consent.granted ? 'Granted' : 'Not granted'}
            {consent.version ? ` · v${consent.version}` : ''}
            {consent.recorded_at ? ` · ${formatDateTime(consent.recorded_at)}` : ''}
          </dd>
          {submission.page && (
            <>
              <dt>Page</dt>
              <dd>{submission.page}</dd>
            </>
          )}
          {submission.referrer && (
            <>
              <dt>Referrer</dt>
              <dd>{submission.referrer}</dd>
            </>
          )}
        </Facts>

        {submission.project_notes && (
          <Notes>
            <strong>Customer notes</strong>
            {submission.project_notes}
          </Notes>
        )}

        {attribution.length > 0 && (
          <Chips>
            {attribution.map(([key, value]) => (
              <span key={key}>{ATTRIBUTION_LABELS[key] || key}: <b>{String(value)}</b></span>
            ))}
          </Chips>
        )}
      </div>
    </Card>
  );
};

const LeadSubmissions = ({ submissions, onOpenImage }) => {
  if (!submissions || submissions.length === 0) return null;
  return (
    <CollapsibleSection
      id="web-submissions"
      title="Website Calculator Submissions"
      icon={<FaCalculator />}
      count={submissions.length}
    >
      {submissions.map((s) => (
        <SubmissionCard key={s.id} submission={s} onOpenImage={onOpenImage} />
      ))}
    </CollapsibleSection>
  );
};

export default LeadSubmissions;
